import React from 'react';
import { Column, CellInfo } from 'react-table';
import Moment from 'moment-timezone';
import { NAMED_DATE_FORMAT } from 'Util/Constants/Common';
import { ClientSideTable } from 'Components/Common/ClientSideTable/ClientSideTable';
import EndorsementDto from 'Models/Endorsement/Dto/EndorsementDto';
import { EDIT_COLUMN_WIDTH } from 'Util/Constants/Tables';
import { FN_EMPTY_VOID } from 'Util/Helpers/Empty';
import FaIcon from 'Components/Common/FaIcon/FaIcon';
import { isEnterKeyPress } from 'Util/Helpers/Input';

interface EndorsementsHistoryPanelProps {
   endorsements: readonly EndorsementDto[];
   isLoading: boolean;
   editEndorsement: (endorsementId: number) => void;
}

interface EndorsementDisplayModel {
   endorsementId: number;
   endorsementTypeDescription: string;
   endorsementStatusDescription: string;
   licenceYearNumber: string;
   startDate: string;
   expiryDate: string;
   onEdit: () => void;
}

const generateEndorsementDisplayData = (
   endorsements: readonly EndorsementDto[],
   editEndorsement: (endorsementId: number) => void
): EndorsementDisplayModel[] => {
   return endorsements.map(
      (endorsement): EndorsementDisplayModel => ({
         endorsementId: endorsement.endorsementId,
         endorsementTypeDescription: endorsement.endorsementTypeDescription,
         endorsementStatusDescription: endorsement.endorsementStatusDescription,
         licenceYearNumber: endorsement.application.licenceYearId ?? '',
         startDate: endorsement.startDate ?? '',
         expiryDate: endorsement.expiryDate ?? '',
         onEdit: (): void => {
            editEndorsement(endorsement.endorsementId);
         },
      })
   );
};

const EndorsementsHistoryPanel = ({
   endorsements,
   isLoading,
   editEndorsement,
}: Readonly<EndorsementsHistoryPanelProps>): JSX.Element => {
   const DateRenderer = (cell: CellInfo): JSX.Element => {
      return (
         <div>
            {cell.value ? Moment(cell.value).format(NAMED_DATE_FORMAT) : 'N/A'}
         </div>
      );
   };

   const COLUMN_HEADERS: Column<EndorsementDisplayModel>[] = [
      {
         Header: 'Endorsement Type',
         accessor: 'endorsementTypeDescription',
      },
      {
         Header: 'Endorsement Status',
         accessor: 'endorsementStatusDescription',
      },
      {
         Header: 'Licensing Year',
         accessor: 'licenceYearNumber',
      },
      {
         Header: 'Start Date',
         accessor: 'startDate',
         Cell: DateRenderer,
      },
      {
         Header: 'Expiry Date',
         accessor: 'expiryDate',
         Cell: DateRenderer,
      },
      {
         Header: <FaIcon icon="ellipsis-h" />,
         id: 'edit',
         width: EDIT_COLUMN_WIDTH,
         className: 'center-icon',
         accessor: FN_EMPTY_VOID,
         Cell: (cellInfo): JSX.Element => {
            return (
               <FaIcon
                  className="small-icon table-row-clickable"
                  icon="edit"
                  onClick={(): void => {
                     cellInfo.original.onEdit();
                  }}
                  onKeyUp={(keyPress): void => {
                     if (isEnterKeyPress(keyPress)) cellInfo.original.onEdit();
                  }}
               />
            );
         },
         sortable: false,
      },
   ];

   return (
      <ClientSideTable
         data={generateEndorsementDisplayData(endorsements, editEndorsement)}
         defaultPageSize={10}
         columns={COLUMN_HEADERS}
         loading={isLoading}
      />
   );
};

export default EndorsementsHistoryPanel;
